import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Camera,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FilePenLine,
  Loader2,
  MessageSquareText,
  Mic,
  Paperclip,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AI_MODEL_OPTIONS, type Client } from '@oneup/shared';
import {
  ApiClientError,
  clientsApi,
  copilotApi,
  type CopilotChatResponse,
  type CopilotEntityMention,
  type CopilotMessage,
  type CopilotPendingConfirmation,
  type CopilotSelectedClientContext,
} from '@/lib/api';
import { applyCopilotApproval } from '@/lib/copilot-approvals';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/ui';
import { useAiSettingsStore, useSelectedAiModel } from '@/stores/ai-settings';
import { useAliceApprovalStore } from '@/stores/alice-approvals';
import { CopilotWriteConfirmCard } from '@/components/copilot-write-confirm-card';
import { MarkdownNotes } from '@/components/markdown-notes';
import { useSpeechDictation, type SpeechDictation } from '@/lib/use-speech-dictation';
import { useAlice } from './alice.context';
import { useAliceAttachments, type AliceAttachment } from './use-alice-attachments';
import { CameraCaptureDialog } from './CameraCaptureDialog';
import type { AliceSuggestedAction } from './alice.types';

type AliceChatMessage = CopilotMessage & {
  /** Frontend-only display metadata; stripped before sending messages to the Worker. */
  clientContext?: CopilotSelectedClientContext | null;
};

export function AlicePanel() {
  const alice = useAlice();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedModel = useSelectedAiModel();
  const disabledConnectors = useAiSettingsStore((state) => state.disabledConnectors);
  const addApproval = useAliceApprovalStore((state) => state.addApproval);
  const removeApproval = useAliceApprovalStore((state) => state.removeApproval);
  const [prompt, setPrompt] = useState('');
  const [selectedClient, setSelectedClient] = useState<CopilotSelectedClientContext | null>(null);
  const [dismissedSuggestedClientKey, setDismissedSuggestedClientKey] = useState<string | null>(
    null,
  );
  const [dismissedMentionKey, setDismissedMentionKey] = useState<string | null>(null);
  const [cursorIndex, setCursorIndex] = useState(0);
  const [highlightedMentionIndex, setHighlightedMentionIndex] = useState(0);
  const [guardAction, setGuardAction] = useState<(() => void) | null>(null);
  const [messages, setMessages] = useState<AliceChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [pending, setPending] = useState<CopilotPendingConfirmation | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [seededContext, setSeededContext] = useState<{
    taskId?: string;
    proposalId?: string;
  }>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const basePromptRef = useRef('');
  const lastVoiceNonce = useRef<number | undefined>(undefined);
  const attachments = useAliceAttachments();
  const [cameraOpen, setCameraOpen] = useState(false);
  const setNotice = useUiStore((state) => state.setNotice);
  const voiceTrigger = useUiStore((state) => state.voiceTrigger);
  const copilotPanelOpen = useUiStore((state) => state.copilotPanelOpen);
  const initialTaskId = useUiStore((state) => state.copilotPanelInitialTaskId);
  const initialProposalId = useUiStore((state) => state.copilotPanelInitialProposalId);
  const seedSignal = useUiStore((state) => state.copilotPanelSeed);
  const consumeSeed = useUiStore((state) => state.consumeCopilotPanelSeed);
  const closeCopilotPanel = useUiStore((state) => state.closeCopilotPanel);

  const dictation = useSpeechDictation((text) => {
    const base = basePromptRef.current;
    setPrompt(base ? `${base} ${text}` : text);
  });

  const taskContextId =
    seededContext.taskId ??
    (alice.useCurrentContext && alice.currentContext.recordType === 'task'
      ? alice.currentContext.recordId
      : undefined);
  const proposalContextId =
    seededContext.proposalId ??
    (alice.useCurrentContext && alice.currentContext.recordType === 'proposal'
      ? alice.currentContext.recordId
      : undefined);

  const usageQuery = useQuery({
    queryKey: ['copilot', 'usage'],
    queryFn: () => copilotApi.usage(),
    enabled: alice.panelState === 'open',
    staleTime: 60_000,
    retry: false,
  });

  const remainingLabel = useMemo(() => {
    const remaining = usageQuery.data?.budget?.remainingCents;
    if (remaining == null) return null;
    return `$${(remaining / 100).toFixed(2)} left today`;
  }, [usageQuery.data]);

  const mentionTrigger = useMemo(
    () => getActiveClientMention(prompt, cursorIndex),
    [cursorIndex, prompt],
  );
  const mentionSearchText = mentionTrigger ? normalizeClientSearchQuery(mentionTrigger.query) : '';
  const mentionKey = mentionTrigger ? `${mentionTrigger.start}:${mentionSearchText}` : null;
  const debouncedMentionSearch = useDebouncedValue(mentionSearchText, 180);
  const mentionDropdownOpen = Boolean(
    !selectedClient &&
      mentionTrigger &&
      mentionSearchText.length > 0 &&
      mentionKey !== dismissedMentionKey,
  );
  const mentionResultsQuery = useQuery({
    queryKey: ['clients', 'alice-mentions', debouncedMentionSearch],
    queryFn: () => clientsApi.list({ q: debouncedMentionSearch }),
    enabled: mentionDropdownOpen && debouncedMentionSearch.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  const mentionOptions = useMemo(
    () => rankClientMentionResults(mentionResultsQuery.data?.items ?? [], mentionSearchText),
    [mentionResultsQuery.data?.items, mentionSearchText],
  );
  const visibleMentionOptions = mentionOptions.slice(0, CLIENT_MENTION_VISIBLE_LIMIT);
  const mentionLoading =
    mentionDropdownOpen &&
    (mentionSearchText !== debouncedMentionSearch || mentionResultsQuery.isFetching);

  const fallbackClientQuery = useMemo(
    () => (selectedClient || mentionTrigger ? '' : detectFallbackClientQuery(prompt)),
    [mentionTrigger, prompt, selectedClient],
  );
  const debouncedFallbackClientQuery = useDebouncedValue(fallbackClientQuery, 300);
  const fallbackClientResultsQuery = useQuery({
    queryKey: ['clients', 'alice-fallback-mention', debouncedFallbackClientQuery],
    queryFn: () => clientsApi.list({ q: debouncedFallbackClientQuery }),
    enabled: debouncedFallbackClientQuery.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  const suggestedClient = useMemo(() => {
    const match = findHighConfidenceClient(
      fallbackClientResultsQuery.data?.items ?? [],
      debouncedFallbackClientQuery,
    );
    if (!match) return null;
    const context = clientToSelectedContext(match);
    const key = clientSuggestionKey(context, debouncedFallbackClientQuery);
    return key === dismissedSuggestedClientKey ? null : context;
  }, [
    debouncedFallbackClientQuery,
    dismissedSuggestedClientKey,
    fallbackClientResultsQuery.data?.items,
  ]);
  const selectedMentionPayload = useMemo(
    () => (selectedClient ? selectedClientToMention(selectedClient) : null),
    [selectedClient],
  );

  useEffect(() => {
    setHighlightedMentionIndex(0);
  }, [mentionSearchText, visibleMentionOptions.length]);

  useEffect(() => {
    if (!alice.composerDirty) return;

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [alice.composerDirty]);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [messages, pending, isStreaming]);

  useEffect(() => {
    if (alice.panelState !== 'open') return;
    const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [alice.panelState]);

  useEffect(() => {
    if (!copilotPanelOpen) return;
    setSeededContext({ taskId: initialTaskId, proposalId: initialProposalId });
    if (alice.panelState !== 'open') alice.setPanelState('open');
  }, [alice, copilotPanelOpen, initialProposalId, initialTaskId]);

  const sendChatTurn = useCallback(
    async (nextMessages: AliceChatMessage[], imageUrls?: string[]) => {
      setIsStreaming(true);
      try {
        const response = await copilotApi.chat({
          messages: stripMessageUiMetadata(nextMessages),
          taskContextId,
          proposalContextId,
          model: selectedModel.model,
          contextRecord: alice.useCurrentContext
            ? alice.currentContext.recordId
              ? {
                  type: alice.currentContext.recordType ?? 'page',
                  id: alice.currentContext.recordId,
                  label: alice.currentContext.recordLabel,
                }
              : { type: 'page', label: alice.currentContext.pageTitle }
            : undefined,
          disabledConnectors: disabledConnectors.length ? disabledConnectors : undefined,
          imageUrls: imageUrls && imageUrls.length ? imageUrls : undefined,
          mentions: selectedMentionPayload ? [selectedMentionPayload] : undefined,
          context: selectedClient ? { selectedClient } : undefined,
        });
        applyChatResponse(response, setMessages, setPending, nextMessages);
        if (response.pendingConfirmation) addApproval(response.pendingConfirmation);
        return response;
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `Warning: ${formatError(err)}` },
        ]);
        setPending(null);
        return null;
      } finally {
        setIsStreaming(false);
      }
    },
    [
      proposalContextId,
      taskContextId,
      selectedModel.model,
      alice.useCurrentContext,
      alice.currentContext,
      disabledConnectors,
      addApproval,
      selectedClient,
      selectedMentionPayload,
    ],
  );

  const sendPrompt = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      const images = attachments.attachments.map((item) => item.dataUrl);
      if ((!trimmed && images.length === 0) || isStreaming || pending) return false;
      const userMessage: AliceChatMessage = {
        role: 'user',
        content: trimmed || 'Please look at the attached image.',
        clientContext: selectedClient,
      };
      setMessages((prev) => {
        const next = [...prev, userMessage];
        void sendChatTurn(next, images);
        return next;
      });
      attachments.clear();
      setConfirmError(null);
      return true;
    },
    [attachments, isStreaming, pending, selectedClient, sendChatTurn],
  );

  useEffect(() => {
    const sig = seedSignal?.nonce;
    if (!sig) return;
    const seed = consumeSeed();
    if (!seed) return;
    sendPrompt(seed);
  }, [consumeSeed, seedSignal, sendPrompt]);

  // Voice hotkey: open Alice (if needed), capture current prompt as the base,
  // and begin dictation. Streams the transcript into the input via the hook.
  useEffect(() => {
    const nonce = voiceTrigger?.nonce;
    if (!nonce || nonce === lastVoiceNonce.current) return;
    lastVoiceNonce.current = nonce;
    if (!dictation.supported) {
      setNotice('Voice dictation needs Chrome or Edge', 'destructive');
      return;
    }
    if (alice.panelState !== 'open') alice.setPanelState('open');
    window.setTimeout(() => textareaRef.current?.focus(), 60);
    if (dictation.listening) {
      dictation.stop();
      return;
    }
    basePromptRef.current = prompt;
    dictation.start();
  }, [voiceTrigger, dictation, alice, prompt, setNotice]);

  // Esc stops dictation without leaving the field.
  useEffect(() => {
    if (!dictation.listening) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') dictation.stop();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dictation]);

  if (!alice.config.enabled || alice.panelState === 'hidden') return null;

  const requestGuarded = (action: () => void) => {
    if (alice.mode === 'composer' && alice.composerDirty) {
      setGuardAction(() => action);
      return;
    }
    action();
  };

  const collapse = () =>
    requestGuarded(() => {
      closeCopilotPanel();
      alice.setPanelState('collapsed');
    });
  const confirmDiscard = () => {
    alice.discardComposerDraft();
    setGuardAction(null);
    guardAction?.();
  };

  if (alice.panelState === 'collapsed') {
    return (
      <AliceRail
        assistantName={alice.config.assistantName}
        avatarPath={alice.config.avatarPath}
        draftActive={alice.mode === 'composer'}
        onExpand={() => alice.setPanelState('open')}
      />
    );
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (sendPrompt(prompt)) setPrompt('');
  };

  const handleSuggestion = (item: AliceSuggestedAction) => {
    if (item.mode === 'composer') {
      alice.runSuggestion(item);
      return;
    }
    sendPrompt(item.description ? `${item.label}: ${item.description}` : item.label);
  };

  const handleClear = () => {
    setMessages([]);
    setPending(null);
    setConfirmError(null);
  };

  const selectClientMention = (client: Client) => {
    if (!mentionTrigger) return;
    const context = clientToSelectedContext(client);
    const handle = context.handle ?? fallbackClientHandle(context.name);
    const token = `@${handle}`;
    const before = prompt.slice(0, mentionTrigger.start);
    const after = prompt.slice(cursorIndex);
    const spacer = after.length > 0 && !/^[\s.,!?;:)]/.test(after) ? ' ' : '';
    const nextPrompt = `${before}${token}${spacer}${after}`;
    const nextCursor = before.length + token.length + spacer.length;

    setPrompt(nextPrompt);
    setSelectedClient(context);
    setDismissedSuggestedClientKey(null);
    setDismissedMentionKey(null);
    setCursorIndex(nextCursor);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const clearSelectedClient = () => {
    setSelectedClient(null);
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const confirmSuggestedClient = (context: CopilotSelectedClientContext) => {
    setSelectedClient(context);
    setDismissedSuggestedClientKey(null);
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const dismissSuggestedClient = (context: CopilotSelectedClientContext) => {
    setDismissedSuggestedClientKey(clientSuggestionKey(context, debouncedFallbackClientQuery));
  };

  const handleMicToggle = () => {
    if (!dictation.supported) {
      setNotice('Voice dictation needs Chrome or Edge', 'destructive');
      return;
    }
    if (dictation.listening) {
      dictation.stop();
      return;
    }
    basePromptRef.current = prompt;
    textareaRef.current?.focus();
    dictation.start();
  };

  const handleFilesPicked = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const images = Array.from(fileList).filter((file) => file.type.startsWith('image/'));
    if (images.length === 0) {
      setNotice('Alice can only read images right now', 'destructive');
      return;
    }
    try {
      await attachments.addFiles(images);
    } catch {
      setNotice('Could not attach that image', 'destructive');
    }
  };

  const handleApplyWrite = async () => {
    if (!pending) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const applied = await applyCopilotApproval(pending);
      setPending(null);
      removeApproval(pending.toolCallId);
      for (const queryKey of applied.invalidate) {
        void queryClient.invalidateQueries({ queryKey });
      }
      setNotice(applied.notice);
      if (applied.navigateTo) navigate(applied.navigateTo);
      const next = [...messages, applied.toolResult];
      setMessages(next);
      await sendChatTurn(next);
    } catch (err) {
      setConfirmError(formatError(err));
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleDiscardWrite = async () => {
    if (!pending) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      const toolResult: CopilotMessage = {
        role: 'tool',
        tool_call_id: pending.toolCallId,
        content: JSON.stringify({
          ok: false,
          cancelled: true,
          reason: 'User discarded the proposed update.',
        }),
      };
      setPending(null);
      removeApproval(pending.toolCallId);
      const next = [...messages, toolResult];
      setMessages(next);
      await sendChatTurn(next);
    } catch (err) {
      setConfirmError(formatError(err));
    } finally {
      setConfirmBusy(false);
    }
  };

  return (
    <div className="flex h-full w-[360px] flex-col bg-card text-card-foreground xl:w-[420px]">
      <AliceHeader
        assistantName={alice.config.assistantName}
        avatarPath={alice.config.avatarPath}
        subtitle={alice.config.assistantSubtitle}
        metaLabel={remainingLabel}
        onCollapse={collapse}
      />

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        {alice.mode === 'composer' && <DraftSessionBanner />}
        {alice.config.contextBarEnabled !== false && <AliceContextBar />}
        {alice.config.suggestedActionsEnabled !== false && alice.config.quickActionsEnabled && (
          <AliceSuggestedActions onSelect={handleSuggestion} />
        )}
        <ChatStream messages={messages} isStreaming={isStreaming} onClear={handleClear} />
        {pending && (
          <div className="mb-4">
            <CopilotWriteConfirmCard
              pending={pending}
              onApply={handleApplyWrite}
              onDiscard={handleDiscardWrite}
              busy={confirmBusy}
              errorMessage={confirmError}
            />
          </div>
        )}
        {alice.mode === 'composer' && <ComposerDraft />}
      </div>

      <form onSubmit={submit} className="border-t border-border bg-background/70 p-3">
        <AliceInputToolbar
          remainingLabel={remainingLabel}
          dictation={dictation}
          onMicToggle={handleMicToggle}
          onPickFiles={() => fileInputRef.current?.click()}
          onOpenCamera={() => setCameraOpen(true)}
          attachmentsFull={attachments.full}
        />
        {attachments.attachments.length > 0 && (
          <AliceAttachmentStrip items={attachments.attachments} onRemove={attachments.remove} />
        )}
        {!selectedClient && suggestedClient && (
          <SuggestedClientContextCard
            client={suggestedClient}
            onConfirm={() => confirmSuggestedClient(suggestedClient)}
            onDismiss={() => dismissSuggestedClient(suggestedClient)}
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(event) => {
            void handleFilesPicked(event.target.files);
            event.target.value = '';
          }}
        />
        {selectedClient && (
          <div className="mt-2">
            <ClientMentionChip client={selectedClient} onClear={clearSelectedClient} />
          </div>
        )}
        <div className="relative mt-2 rounded-lg border border-input bg-background focus-within:ring-1 focus-within:ring-ring">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(event) => {
              setPrompt(event.target.value);
              setCursorIndex(event.currentTarget.selectionStart);
            }}
            onSelect={(event) => setCursorIndex(event.currentTarget.selectionStart)}
            onClick={(event) => setCursorIndex(event.currentTarget.selectionStart)}
            onKeyUp={(event) => setCursorIndex(event.currentTarget.selectionStart)}
            onKeyDown={submitFromKeyboard}
            placeholder="Ask Alice to draft, summarize, find, or update..."
            disabled={isStreaming || Boolean(pending)}
            className="min-h-[96px] max-h-44 w-full resize-y rounded-lg bg-transparent px-3 py-2.5 text-[13px] leading-relaxed outline-none placeholder:text-muted-foreground"
          />
          {mentionDropdownOpen && (
            <ClientMentionDropdown
              options={visibleMentionOptions}
              highlightedIndex={highlightedMentionIndex}
              query={mentionSearchText}
              loading={mentionLoading}
              error={mentionResultsQuery.error}
              total={mentionOptions.length}
              onHighlight={setHighlightedMentionIndex}
              onSelect={selectClientMention}
            />
          )}
          <div className="flex items-center justify-between gap-3 border-t border-border/70 px-2.5 py-2">
            <span className="text-[11px] text-muted-foreground">
              {pending ? 'Review pending update before continuing' : 'Cmd/Ctrl + Enter to send'}
            </span>
            <Button
              type="submit"
              size="sm"
              className="h-8 gap-1.5 px-3"
              aria-label="Send to Alice"
              disabled={
                isStreaming ||
                Boolean(pending) ||
                (prompt.trim().length === 0 && attachments.attachments.length === 0)
              }
            >
              {isStreaming ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Send className="h-3.5 w-3.5" aria-hidden />
              )}
              Send
            </Button>
          </div>
        </div>
      </form>

      <CameraCaptureDialog
        open={cameraOpen}
        onOpenChange={setCameraOpen}
        onCapture={(dataUrl) => attachments.addDataUrl(dataUrl)}
      />

      <Dialog open={Boolean(guardAction)} onOpenChange={(open) => !open && setGuardAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Active Alice draft</DialogTitle>
            <DialogDescription>
              You have an active Alice draft session. Leaving will discard this draft.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setGuardAction(null)}>
              Keep Editing
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDiscard}>
              Discard Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

  function submitFromKeyboard(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionDropdownOpen) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedMentionIndex((index) =>
          visibleMentionOptions.length === 0 ? 0 : (index + 1) % visibleMentionOptions.length,
        );
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedMentionIndex((index) =>
          visibleMentionOptions.length === 0
            ? 0
            : (index - 1 + visibleMentionOptions.length) % visibleMentionOptions.length,
        );
        return;
      }
      if ((event.key === 'Enter' || event.key === 'Tab') && visibleMentionOptions.length > 0) {
        event.preventDefault();
        const selected = visibleMentionOptions[highlightedMentionIndex] ?? visibleMentionOptions[0];
        if (selected) selectClientMention(selected);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        if (mentionKey) setDismissedMentionKey(mentionKey);
        return;
      }
    }

    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  }
}

function AliceRail({
  assistantName,
  avatarPath,
  draftActive,
  onExpand,
}: {
  assistantName: string;
  avatarPath?: string;
  draftActive: boolean;
  onExpand: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onExpand}
      className="flex h-full w-16 flex-col items-center gap-3 bg-card px-2 py-4 text-muted-foreground transition-colors hover:text-foreground"
      aria-label={`Open ${assistantName}`}
    >
      <AliceAvatar avatarPath={avatarPath} assistantName={assistantName} compact />
      <span
        className="text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ writingMode: 'vertical-rl' }}
      >
        {assistantName}
      </span>
      {draftActive && (
        <Badge variant="outline" className="rotate-90 whitespace-nowrap">
          Draft active
        </Badge>
      )}
      <ChevronLeft className="mt-auto h-4 w-4" aria-hidden />
    </button>
  );
}

function AliceHeader({
  assistantName,
  avatarPath,
  subtitle,
  metaLabel,
  onCollapse,
}: {
  assistantName: string;
  avatarPath?: string;
  subtitle?: string;
  metaLabel?: string | null;
  onCollapse: () => void;
}) {
  const backstage = useUiStore((state) => state.backstage);
  return (
    <div className="flex items-start gap-3 border-b border-border px-4 py-3">
      <AliceAvatar avatarPath={avatarPath} assistantName={assistantName} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <h2 className="truncate text-sm font-semibold">{assistantName}</h2>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
              backstage
                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
                : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                backstage ? 'bg-amber-500' : 'bg-emerald-500',
              )}
              aria-hidden
            />
            {backstage ? 'Backstage' : 'Online'}
          </span>
        </div>
        <p className="mt-1 text-[12px] text-muted-foreground">
          {subtitle ?? 'Context-aware help for this workspace.'}
        </p>
        {metaLabel && <p className="mt-0.5 text-[11px] text-muted-foreground">{metaLabel}</p>}
      </div>
      <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={onCollapse}>
        <ChevronRight className="h-4 w-4" aria-hidden />
        <span className="sr-only">Collapse Alice</span>
      </Button>
    </div>
  );
}

function AliceAvatar({
  avatarPath,
  assistantName,
  compact = false,
}: {
  avatarPath?: string;
  assistantName: string;
  compact?: boolean;
}) {
  return (
    <span
      className={cn(
        'flex flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/10 text-primary',
        compact ? 'h-10 w-10' : 'h-11 w-11',
      )}
    >
      {avatarPath ? (
        <img
          src={avatarPath}
          alt={`${assistantName} avatar`}
          className="h-full w-full object-cover"
        />
      ) : (
        <Sparkles className="h-5 w-5" aria-hidden />
      )}
    </span>
  );
}

const COMPOSER_TARGET_LABELS: Record<string, string> = {
  email_reply: 'Email reply',
  client_update: 'Client update',
  task_note: 'Task note',
  proposal_section: 'Proposal section',
  billing_message: 'Billing message',
  call_summary: 'Call summary',
  generic: 'Draft',
};

function DraftSessionBanner() {
  const { composerSession } = useAlice();
  const targetLabel = composerSession
    ? (COMPOSER_TARGET_LABELS[composerSession.targetType] ?? 'Draft')
    : null;
  return (
    <div className="mb-3 flex items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-[12px] text-primary">
      <FilePenLine className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
      <span>Draft session active{targetLabel ? ` - ${targetLabel}` : ''}</span>
    </div>
  );
}

function AliceContextBar() {
  const { currentContext, useCurrentContext, setUseCurrentContext } = useAlice();
  const [expanded, setExpanded] = useState(false);
  const attachedCount = currentContext.contextItems.length + (currentContext.recordId ? 1 : 0);
  const summary = currentContext.recordLabel
    ? `Context: ${currentContext.pageTitle} - ${currentContext.recordLabel}`
    : `Using current page context - ${attachedCount || 1} item${attachedCount === 1 ? '' : 's'}`;

  return (
    <section className="mb-3 rounded-lg border border-border bg-background/70">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] text-muted-foreground"
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
      >
        <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0 text-primary" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{summary}</span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')}
          aria-hidden
        />
      </button>
      {expanded && (
        <div className="border-t border-border px-3 py-3">
          <label className="mb-3 flex items-center justify-between gap-3 text-[12px] text-muted-foreground">
            <span>Attach page context to next message</span>
            <Switch
              checked={useCurrentContext}
              onCheckedChange={setUseCurrentContext}
              aria-label="Use current page as context"
            />
          </label>
          <div className="space-y-1.5 text-[12px]">
            <Fact label="Current page" value={currentContext.pageTitle} />
            <Fact label="Current record" value={currentContext.recordLabel ?? 'None selected'} />
            <Fact label="Organization" value={currentContext.organizationLabel ?? 'Not attached'} />
            <Fact
              label="Attached"
              value={
                useCurrentContext
                  ? currentContext.contextItems.map((item) => item.label).join(', ') ||
                    'Page summary'
                  : 'Disabled for next message'
              }
            />
            <Fact
              label="Warnings"
              value={
                currentContext.blockedActions.length ? 'Some actions require approval' : 'None'
              }
            />
          </div>
        </div>
      )}
    </section>
  );
}

function AliceSuggestedActions({ onSelect }: { onSelect: (item: AliceSuggestedAction) => void }) {
  const { suggestions } = useAlice();
  if (suggestions.length === 0) return null;

  return (
    <section className="mb-3">
      <div className="grid grid-cols-3 gap-1.5">
        {suggestions.slice(0, 3).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item)}
            title={item.description}
            className="min-w-0 rounded-md border border-border bg-background/70 px-2 py-1.5 text-left text-[12px] font-medium text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5"
          >
            <span className="flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 flex-shrink-0 text-primary" aria-hidden />
              <span className="truncate">{item.label}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function ChatStream({
  messages,
  isStreaming,
  onClear,
}: {
  messages: AliceChatMessage[];
  isStreaming: boolean;
  onClear: () => void;
}) {
  const hasMessages = messages.some((message) => message.role !== 'system');
  return (
    <section className="mb-4 min-h-0">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          <MessageSquareText className="h-3.5 w-3.5" aria-hidden />
          Chat
        </div>
        {hasMessages && (
          <button
            type="button"
            onClick={onClear}
            disabled={isStreaming}
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Trash2 className="h-3 w-3" aria-hidden />
            Clear
          </button>
        )}
      </div>
      <div className="space-y-2.5">
        {!hasMessages && !isStreaming && <AliceEmptyChat />}
        {messages.map((message, idx) => (
          <MessageRow key={idx} message={message} />
        ))}
        {isStreaming && <ThinkingRow />}
      </div>
    </section>
  );
}

function AliceModelSwitcher({ remainingLabel }: { remainingLabel?: string | null }) {
  const selectedModel = useSelectedAiModel();
  const setModelId = useAiSettingsStore((state) => state.setModelId);
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={
            remainingLabel
              ? `${remainingLabel} • Click to change model`
              : 'Click to change Alice model'
          }
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
        >
          <Sparkles className="h-3 w-3 text-primary" aria-hidden />
          <span className="max-w-[150px] truncate">{selectedModel.label}</span>
          <ChevronDown className="h-3 w-3" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-1.5">
        <p className="px-2 py-1.5 text-[11px] font-medium text-muted-foreground">Alice model</p>
        {AI_MODEL_OPTIONS.map((option) => {
          const active = option.id === selectedModel.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                setModelId(option.id);
                setOpen(false);
              }}
              className={cn(
                'flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors',
                active ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/60',
              )}
            >
              <span className="flex items-center gap-1.5 text-[12px] font-medium">
                {active && <Sparkles className="h-3 w-3 text-primary" aria-hidden />}
                {option.label}
              </span>
              <span className="text-[11px] leading-4 text-muted-foreground">
                {option.description}
              </span>
            </button>
          );
        })}
        <p className="px-2 pb-1 pt-1.5 text-[10px] leading-3.5 text-muted-foreground">
          Saved on this device. Validated server-side before use.
        </p>
      </PopoverContent>
    </Popover>
  );
}

function AliceInputToolbar({
  remainingLabel,
  dictation,
  onMicToggle,
  onPickFiles,
  onOpenCamera,
  attachmentsFull,
}: {
  remainingLabel?: string | null;
  dictation: SpeechDictation;
  onMicToggle: () => void;
  onPickFiles: () => void;
  onOpenCamera: () => void;
  attachmentsFull: boolean;
}) {
  const { config } = useAlice();
  const micEnabled = config.voiceEnabledPlaceholder !== false && dictation.supported;
  const filesEnabled = config.fileUploadEnabledPlaceholder !== false;
  const cameraEnabled =
    config.cameraEnabledPlaceholder !== false &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia);
  return (
    <div className="flex items-center gap-1.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onMicToggle}
        className={cn('h-8 w-8', dictation.listening && 'text-destructive')}
        title={
          dictation.supported
            ? dictation.listening
              ? 'Stop voice dictation'
              : 'Start voice dictation (V)'
            : 'Voice needs Chrome or Edge'
        }
        aria-label={dictation.listening ? 'Stop voice dictation' : 'Start voice dictation'}
        aria-pressed={dictation.listening}
        disabled={!micEnabled}
      >
        <Mic className={cn('h-4 w-4', dictation.listening && 'animate-pulse')} aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onPickFiles}
        className="h-8 w-8"
        title={attachmentsFull ? 'Attachment limit reached (4)' : 'Attach an image for Alice'}
        aria-label="Attach an image"
        disabled={!filesEnabled || attachmentsFull}
      >
        <Paperclip className="h-4 w-4" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onOpenCamera}
        className="h-8 w-8"
        title={
          cameraEnabled
            ? attachmentsFull
              ? 'Attachment limit reached (4)'
              : 'Capture a photo for Alice'
            : 'Camera not available'
        }
        aria-label="Capture a photo"
        disabled={!cameraEnabled || attachmentsFull}
      >
        <Camera className="h-4 w-4" aria-hidden />
      </Button>
      <AliceModelSwitcher remainingLabel={remainingLabel} />
    </div>
  );
}

function AliceAttachmentStrip({
  items,
  onRemove,
}: {
  items: AliceAttachment[];
  onRemove: (id: string) => void;
}) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {items.map((item) => (
        <div
          key={item.id}
          className="relative h-14 w-14 overflow-hidden rounded-md border border-border bg-muted"
        >
          <img src={item.dataUrl} alt="Attachment preview" className="h-full w-full object-cover" />
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            className="absolute right-0.5 top-0.5 rounded-full bg-background/90 p-0.5 text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Remove attachment"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </div>
      ))}
    </div>
  );
}

function SuggestedClientContextCard({
  client,
  onConfirm,
  onDismiss,
}: {
  client: CopilotSelectedClientContext;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const secondary = [client.handle ? `@${client.handle}` : null, client.voiceCode]
    .filter(Boolean)
    .join(' / ');
  return (
    <section className="mt-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-[12px]">
      <div className="flex items-center gap-2">
        <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-foreground">Suggested client: {client.name}</p>
          {secondary && <p className="mt-0.5 text-[11px] text-muted-foreground">{secondary}</p>}
        </div>
        <button
          type="button"
          onClick={onConfirm}
          className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground transition-colors hover:border-primary/40"
        >
          <Check className="h-3 w-3" aria-hidden />
          Use
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
          aria-label="Dismiss suggested client"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </section>
  );
}

function ClientMentionChip({
  client,
  onClear,
}: {
  client: CopilotSelectedClientContext;
  onClear: () => void;
}) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-primary/20 bg-primary/10 px-2 py-1 text-[12px] font-medium text-primary">
      <Building2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
      <span className="min-w-0 truncate">@{client.name}</span>
      <button
        type="button"
        onClick={onClear}
        className="rounded-sm p-0.5 text-primary/70 transition-colors hover:bg-primary/10 hover:text-primary"
        aria-label="Remove client mention"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}

function SentClientContextSummary({ client }: { client: CopilotSelectedClientContext }) {
  const [expanded, setExpanded] = useState(false);
  const secondary = [client.handle ? `@${client.handle}` : null, client.voiceCode]
    .filter(Boolean)
    .join(' / ');
  const aliases = client.aliases?.slice(0, 5).join(', ');

  return (
    <div className="max-w-full overflow-hidden rounded-md border border-primary/20 bg-background/90 text-[11px] text-foreground shadow-sm">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-muted-foreground transition-colors hover:text-foreground"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 flex-shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3 flex-shrink-0" aria-hidden />
        )}
        <Building2 className="h-3 w-3 flex-shrink-0 text-primary" aria-hidden />
        <span className="min-w-0 truncate">
          Client context: {client.name}
          {secondary ? ` / ${secondary}` : ''}
        </span>
      </button>
      {expanded && (
        <div className="grid gap-1 border-t border-border px-2 py-1.5 text-muted-foreground">
          <Fact label="Client" value={client.name} />
          {secondary && <Fact label="Scope" value={secondary} />}
          {client.website && <Fact label="Website" value={client.website} />}
          {client.billingEmail && <Fact label="Billing" value={client.billingEmail} />}
          {aliases && <Fact label="Aliases" value={aliases} />}
        </div>
      )}
    </div>
  );
}

function ClientMentionDropdown({
  options,
  highlightedIndex,
  query,
  loading,
  error,
  total,
  onHighlight,
  onSelect,
}: {
  options: Client[];
  highlightedIndex: number;
  query: string;
  loading: boolean;
  error: unknown;
  total: number;
  onHighlight: (index: number) => void;
  onSelect: (client: Client) => void;
}) {
  return (
    <div className="mx-2 mb-2 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg">
      <div className="border-b border-border px-2.5 py-1.5 text-[11px] text-muted-foreground">
        Client mention search: @{query}
      </div>
      <div className="max-h-64 overflow-y-auto p-1">
        {loading && options.length === 0 && (
          <div className="flex items-center gap-2 px-2 py-2 text-[12px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Searching clients...
          </div>
        )}
        {!loading && Boolean(error) && (
          <div className="px-2 py-2 text-[12px] text-destructive">
            Client search failed. You can still send a normal message.
          </div>
        )}
        {!loading && !error && options.length === 0 && (
          <div className="px-2 py-2 text-[12px] text-muted-foreground">No matching clients.</div>
        )}
        {options.map((client, index) => {
          const active = index === highlightedIndex;
          const aliases = parseClientAliases(client.clientAliases).slice(0, 3).join(', ');
          return (
            <button
              key={client.id}
              type="button"
              onMouseEnter={() => onHighlight(index)}
              onMouseDown={(event) => {
                event.preventDefault();
                onSelect(client);
              }}
              className={cn(
                'flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors',
                active ? 'bg-primary/10 text-foreground' : 'hover:bg-muted/60',
              )}
            >
              <span className="flex min-w-0 items-center gap-1.5 text-[12px] font-medium">
                <Building2 className="h-3.5 w-3.5 flex-shrink-0 text-primary" aria-hidden />
                <span className="truncate">{client.name}</span>
              </span>
              <span className="text-[11px] text-muted-foreground">
                {[client.clientHandle ? `@${client.clientHandle}` : null, client.voiceCode]
                  .filter(Boolean)
                  .join(' / ') || 'No handle yet'}
              </span>
              {aliases && (
                <span className="line-clamp-1 text-[11px] text-muted-foreground">
                  Aliases: {aliases}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {loading && options.length > 0 && (
        <div className="border-t border-border px-2.5 py-1.5 text-[11px] text-muted-foreground">
          Refreshing matches...
        </div>
      )}
      {total > CLIENT_MENTION_VISIBLE_LIMIT && (
        <div className="border-t border-border px-2.5 py-1.5 text-[11px] text-muted-foreground">
          Showing first {CLIENT_MENTION_VISIBLE_LIMIT} of {total} matches. Keep typing to narrow.
        </div>
      )}
    </div>
  );
}

function ComposerDraft() {
  const { composerDraft, setComposerDraft } = useAlice();
  return (
    <section className="mb-4 rounded-lg border border-border bg-background/70 p-3">
      <div className="mb-2 flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        <FilePenLine className="h-3.5 w-3.5" aria-hidden />
        Composer Draft
      </div>
      <textarea
        value={composerDraft}
        onChange={(event) => setComposerDraft(event.target.value)}
        placeholder="Draft with Alice..."
        className="min-h-40 w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-[13px] leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      <p className="mt-2 text-[11px] text-muted-foreground">
        Drafts stay local until you apply them. Saved drafts are coming soon.
      </p>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-foreground">{value}</span>
    </div>
  );
}

function AliceEmptyChat() {
  return (
    <div className="rounded-lg border border-border bg-background/70 px-3 py-3 text-[12px] text-muted-foreground">
      <p className="font-medium text-foreground">
        Ask Alice about tasks, records, or next actions.
      </p>
      <p className="mt-1">Writes still require confirmation before anything changes in Airtable.</p>
    </div>
  );
}

function ThinkingRow() {
  return (
    <div className="flex items-center gap-2 px-2 py-1 text-[12px] text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      Thinking...
    </div>
  );
}

function MessageRow({ message }: { message: AliceChatMessage }) {
  if (message.role === 'system') return null;

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="flex max-w-[86%] flex-col items-end gap-1.5">
          {message.clientContext && <SentClientContextSummary client={message.clientContext} />}
          <div className="rounded-lg bg-primary px-3 py-2 text-[13px] leading-relaxed text-primary-foreground whitespace-pre-wrap">
            {message.content ?? ''}
          </div>
        </div>
      </div>
    );
  }

  if (message.role === 'tool') return <ToolResultChip message={message} />;

  const hasText = typeof message.content === 'string' && message.content.trim().length > 0;
  const toolCalls = message.tool_calls ?? [];
  if (!hasText && toolCalls.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5">
      {toolCalls.map((call) => (
        <div
          key={call.id}
          className="inline-flex w-fit items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground"
        >
          <span aria-hidden>-&gt;</span>
          <span className="font-mono">{call.function.name}(...)</span>
        </div>
      ))}
      {hasText && (
        <div className="max-w-[92%] rounded-lg border border-border bg-background/70 px-3 py-2 text-[13px] leading-relaxed text-foreground">
          <MarkdownNotes
            notes={message.content}
            emptyState={null}
            className="break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
          />
        </div>
      )}
    </div>
  );
}

function ToolResultChip({ message }: { message: CopilotMessage }) {
  const [expanded, setExpanded] = useState(false);
  const content = typeof message.content === 'string' ? message.content : '';
  const summary = useMemo(() => summarizeToolResult(message, content), [message, content]);
  const pretty = useMemo(() => prettyJson(content), [content]);

  return (
    <div className="rounded-md border border-border bg-card text-[12px]">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-muted-foreground transition-colors hover:text-foreground"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden />
        )}
        <span className="font-mono text-[11px]">{summary}</span>
      </button>
      {expanded && (
        <pre className="max-h-72 overflow-auto border-t border-border bg-background/60 px-2.5 py-2 text-[11px] leading-relaxed text-foreground whitespace-pre-wrap">
          {pretty}
        </pre>
      )}
    </div>
  );
}

function applyChatResponse(
  response: CopilotChatResponse,
  setMessages: (messages: AliceChatMessage[]) => void,
  setPending: (pending: CopilotPendingConfirmation | null) => void,
  localMessages: AliceChatMessage[],
) {
  setMessages(mergeMessageClientContexts(response.messages ?? [], localMessages));
  setPending(response.pendingConfirmation ?? null);
}

function stripMessageUiMetadata(messages: AliceChatMessage[]): CopilotMessage[] {
  return messages.map(({ clientContext: _clientContext, ...message }) => message);
}

function mergeMessageClientContexts(
  responseMessages: CopilotMessage[],
  localMessages: AliceChatMessage[],
): AliceChatMessage[] {
  const userContexts = localMessages
    .filter((message) => message.role === 'user')
    .map((message) => message.clientContext ?? null);
  let userIndex = 0;

  return responseMessages.map((message) => {
    if (message.role !== 'user') return message;
    const clientContext = userContexts[userIndex] ?? null;
    userIndex += 1;
    return clientContext ? { ...message, clientContext } : message;
  });
}

const CLIENT_MENTION_VISIBLE_LIMIT = 6;
const FALLBACK_CLIENT_STOP_WORDS = new Set(['the', 'this', 'that', 'their', 'them', 'client']);

interface ActiveClientMention {
  start: number;
  query: string;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function getActiveClientMention(value: string, cursorIndex: number): ActiveClientMention | null {
  if (cursorIndex < 0 || cursorIndex > value.length) return null;
  const beforeCursor = value.slice(0, cursorIndex);
  const atIndex = beforeCursor.lastIndexOf('@');
  if (atIndex < 0) return null;
  const previous = atIndex > 0 ? value[atIndex - 1] : '';
  if (previous && /[A-Za-z0-9_]/.test(previous)) return null;

  const query = beforeCursor.slice(atIndex + 1);
  if (!query || query.length > 80 || query.includes('\n')) return null;
  if (/^\s/.test(query) || /[!?.,;:]$/.test(query)) return null;
  return { start: atIndex, query };
}

function normalizeClientSearchQuery(value: string): string {
  return value
    .replace(/^@/, '')
    .replace(/[!?.,;:]+$/g, '')
    .trim()
    .slice(0, 80);
}

function parseClientAliases(value: string | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map((alias) => alias.trim())
    .filter(Boolean);
}

function clientToSelectedContext(client: Client): CopilotSelectedClientContext {
  return {
    id: client.id,
    name: client.name,
    handle: normalizeClientHandle(client.clientHandle) ?? fallbackClientHandle(client.name),
    voiceCode: cleanOptionalString(client.voiceCode),
    aliases: parseClientAliases(client.clientAliases),
    status: cleanOptionalString(client.status),
    website: cleanOptionalString(client.website),
    primaryContact: cleanOptionalString(client.billingContactName ?? client.contactNames?.[0]),
    billingEmail: cleanOptionalString(client.allBillingEmails?.[0] ?? client.orgContactEmail),
    stripeCustomerId: cleanOptionalString(client.stripeCustomerId),
  };
}

function selectedClientToMention(client: CopilotSelectedClientContext): CopilotEntityMention {
  return {
    type: 'organization',
    id: client.id,
    name: client.name,
    handle: client.handle,
    voiceCode: client.voiceCode,
  };
}

function normalizeClientHandle(value: string | undefined): string | undefined {
  const handle = cleanOptionalString(value)?.replace(/^@/, '');
  return handle || undefined;
}

function fallbackClientHandle(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .slice(0, 48) || 'client'
  );
}

function cleanOptionalString(value: string | undefined): string | undefined {
  const clean = value?.trim();
  return clean ? clean : undefined;
}

function rankClientMentionResults(clients: Client[], query: string): Client[] {
  return [...clients].sort((a, b) => {
    const scoreA = scoreClientMentionResult(a, query);
    const scoreB = scoreClientMentionResult(b, query);
    if (scoreA !== scoreB) return scoreA - scoreB;
    return a.name.localeCompare(b.name);
  });
}

function scoreClientMentionResult(client: Client, query: string): number {
  const normalizedQuery = normalizeClientMatchText(query);
  const compactQuery = compactClientMatchText(query);
  if (!normalizedQuery || !compactQuery) return 99;

  const name = normalizeClientMatchText(client.name);
  const compactName = compactClientMatchText(client.name);
  const handle = compactClientMatchText(client.clientHandle);
  const voiceCode = compactClientMatchText(client.voiceCode);
  const aliases = parseClientAliases(client.clientAliases);
  const aliasTexts = aliases.map(normalizeClientMatchText);
  const aliasCompacts = aliases.map(compactClientMatchText);
  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  const candidateTexts = [name, ...aliasTexts].filter(Boolean);

  if (handle && handle === compactQuery) return 0;
  if (voiceCode && voiceCode === compactQuery) return 1;
  if (compactName === compactQuery) return 2;
  if (aliasCompacts.some((alias) => alias === compactQuery)) return 3;
  if (handle?.startsWith(compactQuery)) return 4;
  if (voiceCode?.startsWith(compactQuery)) return 5;
  if (name.includes(normalizedQuery)) return 6;
  if (aliasTexts.some((alias) => alias.includes(normalizedQuery))) return 7;
  if (
    queryTokens.length > 0 &&
    candidateTexts.some((candidate) => queryTokens.every((token) => candidate.includes(token)))
  ) {
    return 8;
  }
  return 99;
}

function normalizeClientMatchText(value: string | undefined): string {
  return (value ?? '')
    .toLowerCase()
    .replace(/^@/, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function compactClientMatchText(value: string | undefined): string {
  return normalizeClientMatchText(value).replace(/\s+/g, '');
}

function detectFallbackClientQuery(prompt: string): string {
  const atMatches = [...prompt.matchAll(/@([A-Za-z0-9][A-Za-z0-9_-]{1,60})\b/g)];
  const atCandidate = atMatches.at(-1)?.[1];
  if (atCandidate) return normalizeClientSearchQuery(atCandidate);

  const forMatches = [...prompt.matchAll(/\bfor\s+@?([A-Za-z0-9][A-Za-z0-9_-]{1,60})\b/gi)];
  const forCandidate = forMatches.at(-1)?.[1];
  if (!forCandidate) return '';
  const normalized = normalizeClientSearchQuery(forCandidate);
  if (FALLBACK_CLIENT_STOP_WORDS.has(normalized.toLowerCase())) return '';
  return normalized;
}

function findHighConfidenceClient(clients: Client[], query: string): Client | null {
  if (!query) return null;
  const ranked = rankClientMentionResults(clients, query).filter(
    (client) => scoreClientMentionResult(client, query) <= 3,
  );
  return ranked.length === 1 ? (ranked[0] ?? null) : null;
}

function clientSuggestionKey(client: CopilotSelectedClientContext, query: string): string {
  return `${client.id}:${normalizeClientSearchQuery(query).toLowerCase()}`;
}

function formatError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === 'budget_exceeded') return `Daily budget exceeded: ${err.message}`;
    if (err.code === 'ai_unavailable') return `AI temporarily unavailable: ${err.message}`;
    if (err.code === 'openai_not_configured') {
      return 'OpenAI is not configured on the Worker. Check OPENAI_API_KEY.';
    }
    return `${err.code}: ${err.message}`;
  }
  if (err instanceof Error) return err.message;
  return 'Request failed.';
}

function summarizeToolResult(message: CopilotMessage, content: string): string {
  const named = typeof message.name === 'string' && message.name ? message.name : null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    parsed = null;
  }

  const toolName = named ?? guessToolNameFromShape(parsed);
  const tail = describeToolResult(toolName, parsed);
  return `${toolName ? `${toolName} ->` : 'tool ->'} ${tail}`.trim();
}

function guessToolNameFromShape(parsed: unknown): string | null {
  if (!parsed || typeof parsed !== 'object') return null;
  const obj = parsed as Record<string, unknown>;
  if (Array.isArray(obj.tasks)) return 'search_tasks';
  if (Array.isArray(obj.clients)) return 'find_client';
  if (Array.isArray(obj.proposals)) return 'proposal_search';
  if (typeof obj.subject === 'string' && typeof obj.body === 'string') return 'draft_client_update';
  if (typeof obj.cancelled === 'boolean') return 'update_task';
  if (typeof obj.proposalId === 'string' && obj.opened === true) return 'openProposal';
  if (typeof obj.proposalId === 'string' && typeof obj.patched === 'object')
    return 'updateProposalFields';
  if (typeof obj.taskId === 'string' && typeof obj.patched === 'object') return 'update_task';
  if (typeof obj.id === 'string' && typeof obj.name === 'string' && obj.client !== undefined) {
    return 'get_task';
  }
  return null;
}

function describeToolResult(toolName: string | null, parsed: unknown): string {
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    if (Array.isArray(obj.tasks)) {
      const total = typeof obj.total === 'number' ? obj.total : obj.tasks.length;
      return `${total} result${total === 1 ? '' : 's'}`;
    }
    if (Array.isArray(obj.clients)) {
      const total = typeof obj.total === 'number' ? obj.total : obj.clients.length;
      return `${total} client${total === 1 ? '' : 's'}`;
    }
    if (Array.isArray(obj.proposals)) {
      const total = typeof obj.total === 'number' ? obj.total : obj.proposals.length;
      return `${total} proposal${total === 1 ? '' : 's'}`;
    }
    if (toolName === 'get_task' && typeof obj.name === 'string') return obj.name;
    if (toolName === 'draft_client_update') return 'draft ready';
    if (
      toolName === 'update_task' ||
      toolName === 'updateProposalFields' ||
      toolName === 'createProposalDraft' ||
      toolName === 'openProposal'
    ) {
      if (obj.cancelled === true) return 'cancelled by user';
      if (obj.ok === true) return 'applied';
      if (obj.ok === false) return 'declined';
    }
  }
  return 'result';
}

function prettyJson(content: string): string {
  try {
    return JSON.stringify(JSON.parse(content), null, 2);
  } catch {
    return content;
  }
}
